/**
 * How the console writes things (pure: no DOM, no requests): escaping, money Q1,234.56 / US$,
 * dates «12 oct 2026» (es-GT), and the Spanish names of roles, states and products (F6).
 */

export type Money = { amountMinor: number; currency: 'GTQ' | 'USD' };
/** Quetzales and dollars never share a total or an axis: figures carry their currency. */
export type Cur = 'GTQ' | 'USD';

export const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
export const iso = (d: Date) => d.toISOString().slice(0, 10);
export const icon = (name: string, cls = 'icon') =>
  `<svg class="${cls}" aria-hidden="true"><use href="#i-${name}"/></svg>`;
export const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * An id as a person can read it (#235, M11): `COT-dc86d7b9-…-bc1333bc850b` → `COT-…33bc850b`
 * (its prefix and the last 8 characters of its UUID); an id that is already short stays.
 */
export const shortId = (id: string) => {
  const m =
    /^([A-Z]+)-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}([0-9a-f]{8})$/i.exec(id);
  return m ? `${m[1]}-…${m[2]}` : id;
};

/**
 * A quote's number as people and carriers say it (#259): the agency's number with 6 digits,
 * `COT-000123` (and more digits once it passes 999999).
 */
export const quoteNumber = (n: number) => `COT-${String(n).padStart(6, '0')}`;

/** A cash box by its person, not its id: `caja-sofia-rac` → «Caja de Sofia Rac». */
export const boxName = (id: string) =>
  `Caja de ${id
    .replace(/^caja-/, '')
    .split('-')
    .filter(Boolean)
    .map(cap)
    .join(' ')}`;

const MONEY = new Intl.NumberFormat('es-GT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
/** Q1,234.56 · −Q1,234.56 · US$1,234.56 */
export const q = (minor: number, currency = 'GTQ') =>
  `${minor < 0 ? '−' : ''}${currency === 'GTQ' ? 'Q' : 'US$'}${MONEY.format(Math.abs(minor) / 100)}`;
export const fmt = (m: Money | null | undefined) => (m ? q(m.amountMinor, m.currency) : '—');
const DATE = new Intl.DateTimeFormat('es-GT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
/** "2026-10-12" → "12 oct 2026" (read at noon so no time zone moves the day). */
export const date = (d: string | null | undefined) =>
  d ? DATE.format(new Date(`${d.slice(0, 10)}T12:00:00`)).replace('.', '') : '—';
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => w.length > 2 || /^[A-ZÁÉÍÓÚ]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

export const ROLE: Record<string, string> = {
  agency_director: 'Dirección',
  agency_admin: 'Administración',
  agency_manager: 'Gerencia',
  cashier: 'Caja',
  collections_officer: 'Cobros',
  evaluator: 'Evaluación',
  training_manager: 'Formación',
  agent: 'Agente',
  // Nubi's own organization (ADR-0023).
  nubi_admin: 'Administración de Nubi',
  nubi_ops: 'Operaciones de Nubi',
};
export const STATUS: Record<string, string> = {
  DUE: 'Por cobrar',
  OVERDUE: 'Vencido',
  DELINQUENT: 'En mora',
  PAID: 'Pagado',
  CANCELLED: 'Anulado',
  PREPARED: 'Preparada',
  APPROVED: 'Aprobada',
  SCHEDULED: 'Programado para transferencia',
  REJECTED: 'Rechazada',
  OPEN: 'Abierta',
  CLOSED: 'Cerrada',
  PENDING_APPROVAL: 'Por aprobar',
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  SETTLED: 'Aplicada',
  ANNULLED: 'Anulada',
  APPLIED: 'Aplicado',
  PENDING: 'Pendiente',
  MATCHED: 'Coincide',
  DIFFERENT_AMOUNT: 'Monto distinto',
  DUPLICATE_IN_STATEMENT: 'Duplicada',
  UNEXPECTED_IN_STATEMENT: 'No esperada',
  MISSING_IN_STATEMENT: 'Falta en el estado',
  REQUESTED: 'Solicitada',
  SENT_TO_CARRIER: 'Enviada',
  IN_PROGRESS: 'En proceso',
  WAITING_AGENCY: 'Pide información',
  DONE: 'Completada',
};
export const DECISION: Record<string, string> = {
  ACCEPT: 'Acepta',
  REFER: 'En revisión',
  DECLINE: 'Rechaza',
};
export const REASON: Record<string, string> = {
  PAYMENT_FREQUENCY: 'no acepta esa forma de pago',
  DRIVER_AGE: 'edad del conductor',
  VEHICLE_AGE: 'antigüedad del vehículo',
  VEHICLE_TYPE: 'tipo de vehículo',
  VEHICLE_USE: 'uso del vehículo',
  SUM_INSURED: 'suma asegurada',
  CLAIMS_HISTORY: 'siniestralidad',
  TRACKER_REQUIRED: 'requiere GPS',
  MAX_MEMBERS: 'demasiados asegurados',
  MEMBER_AGE: 'edad de un asegurado',
  PRE_EXISTING_CONDITION: 'padecimiento preexistente',
  HOSPITALIZATION: 'hospitalización reciente',
  EXCLUSION_APPLIED: 'aplica una exclusión',
  COVERAGE_DEPENDENCY: 'una cobertura requiere otra',
  MANDATORY_COVERAGE: 'falta una cobertura obligatoria',
  LIABILITY_LIMIT: 'límite de responsabilidad civil',
  MINIMUM_PREMIUM: 'prima mínima',
  CONSTRUCTION_ZONE: 'construcción o zona',
  BUILDING_VALUE: 'valor de la construcción',
  CONTENTS_VALUE: 'valor de los contenidos',
  BUSINESS_ACTIVITY: 'actividad del negocio',
  TOTAL_VALUE: 'valor total asegurado',
  EMPLOYEES: 'número de empleados',
  AGE_AND_TERM: 'edad más plazo',
  BODY_MASS_INDEX: 'índice de masa corporal',
  RIDER_AGE: 'edad para la cobertura adicional',
  OCCUPATION_RISK: 'riesgo de la ocupación',
  INCOMPLETE_REQUEST: 'solicitud incompleta',
  FORCED_BY_CARRIER: 'decisión de la aseguradora',
  TOBACCO_USE: 'consumo de tabaco',
  FAMILY_GROUP: 'grupo familiar',
};

const AGENCY: Record<string, string> = {
  morales: 'Agencia Morales',
  castillo: 'Agencia Castillo',
};
/** An agency's name; outside an agency (null), Nubi. */
export const agencyName = (id: string | null) => (id ? (AGENCY[id] ?? id) : 'Nubi');

export const FREQUENCY: Record<string, string> = {
  ANNUAL: 'Anual',
  SEMIANNUAL: 'Semestral',
  QUARTERLY: 'Trimestral',
  MONTHLY: 'Mensual',
};

/** The current month in Guatemala ("2026-10"), not in UTC: 18:00 on the last day is still that month. */
const GT_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala' });
export const guatemalaMonth = (d: Date) => GT_DAY.format(d).slice(0, 7);

const WHEN = new Intl.DateTimeFormat('es-GT', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'America/Guatemala',
});
export const when = (i: string) => WHEN.format(new Date(i)).replace('.', '');
/** "human:member-test-morales-admin" → "admin"; free text (people at the carrier) as typed. */
export const person = (p: string | null) =>
  !p
    ? 'Sin asignar'
    : p === 'system:nubi'
      ? 'Nubi'
      : p === 'candidate:portal'
        ? 'la candidata, en su postulación'
        : p === 'candidate:boleta'
          ? 'la persona, en la boleta'
          : p.startsWith('human:')
            ? p.slice(6).replace(/^member-test-\w+-/, '')
            : p;

export const PRODUCT: Record<string, string> = {
  AUTO: 'Autos',
  AUTO_LIABILITY: 'Responsabilidad civil de autos',
  PROPERTY: 'Incendio y hogar',
  LIFE: 'Vida',
  HEALTH: 'Gastos médicos',
  PERSONAL_ACCIDENT: 'Accidentes personales',
  HOME: 'Hogar',
  SME: 'Pymes',
  SURETY: 'Fianzas',
};
/** The product line's Spanish name: shared by every view that lists them. */
export const lineName = (l: string) => PRODUCT[l] ?? l;
/**
 * The agents' names by their code (#313), loaded once per session from the team's directory
 * (`/v1/team/directory`): the console names an agent by their name everywhere — policies,
 * customers, commissions, statistics — and by their code (capitalized) only when the agency has
 * no name for it.
 */
let agentNames: Record<string, string> = {};
export function setAgentNames(names: Record<string, string>) {
  agentNames = { ...names };
}
export const agentName = (a: string) => agentNames[a] ?? cap(a);
export const CURRENCY_NAME: Record<Cur, string> = { GTQ: 'quetzales', USD: 'dólares' };

const PERIOD = new Intl.DateTimeFormat('es-GT', { month: 'long', year: 'numeric' });
/** "2026-10" → "Octubre 2026" (read at noon so no time zone moves the month). */
export const period = (p: string) => cap(PERIOD.format(new Date(`${p}-15T12:00:00`)));
